/**
 * Bluetooth ESC/POS Thermal Printer Helper for Panashe Coaches
 * Supports 58mm / 80mm portable Bluetooth thermal receipt printers via Web Bluetooth API.
 */

const ESC = '\x1B';
const GS = '\x1D';
import { getSystemDetailsUrl } from './qrCode';

const CMD = {
  RESET: ESC + '@',
  ALIGN_LEFT: ESC + 'a0',
  ALIGN_CENTER: ESC + 'a1',
  ALIGN_RIGHT: ESC + 'a2',
  BOLD_ON: ESC + 'E1',
  BOLD_OFF: ESC + 'E0',
  DOUBLE_SIZE_ON: ESC + '!\x30',
  DOUBLE_SIZE_OFF: ESC + '!\x00',
  UNDERLINE_ON: ESC + '-\x01',
  UNDERLINE_OFF: ESC + '-\x00',
  FEED_CUT: GS + 'V\x41\x03', // Cut paper
};

function encodeText(text) {
  const encoder = new TextEncoder();
  return encoder.encode(text);
}


function encodeQrCommands(data) {
  const dataLength = new TextEncoder().encode(data).length + 3;
  return GS + '(k' + String.fromCharCode(4, 0, 49, 65, 50, 0) +
    GS + '(k' + String.fromCharCode(3, 0, 49, 67, 6) +
    GS + '(k' + String.fromCharCode(3, 0, 49, 69, 49) +
    GS + '(k' + String.fromCharCode(dataLength & 0xff, dataLength >> 8, 49, 80, 48) + data +
    GS + '(k' + String.fromCharCode(3, 0, 49, 81, 48);
}

/**
 * Format passenger ticket into ESC/POS thermal receipt commands
 */
export function formatTicketEscPos(booking, trip, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  let commands = '';

  commands += CMD.RESET;
  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += CMD.DOUBLE_SIZE_ON;
  commands += 'PANASHE COACHES\n';
  commands += CMD.DOUBLE_SIZE_OFF;
  commands += 'Cross-Border Passenger Ticket\n';
  commands += '================================\n';
  commands += CMD.BOLD_OFF;

  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += `REF: ${booking.booking_ref || ''}\n`;
  commands += `STATUS: ${(booking.status || 'CONFIRMED').toUpperCase()}\n`;
  commands += CMD.BOLD_OFF;
  commands += '--------------------------------\n';

  commands += CMD.ALIGN_LEFT;
  const passenger = booking.passengers?.[0] || {};
  commands += CMD.BOLD_ON + 'PASSENGER DETAILS:\n' + CMD.BOLD_OFF;
  commands += `Name: ${passenger.full_name || 'N/A'}\n`;
  commands += `Phone: ${passenger.phone || 'N/A'}\n`;
  commands += `Passport No: ${passenger.passport_number || (passenger.is_local ? 'Local (Exempt)' : 'N/A')}\n`;
  commands += `Seat(s): ${booking.seat_numbers?.join(', ') || 'N/A'}\n`;
  commands += `Next of Kin: ${passenger.next_of_kin_name || 'N/A'}\n`;
  commands += `Kin Phone: ${passenger.next_of_kin_phone || 'N/A'}\n`;
  if (passenger.boarding_point || booking.pickup_point) commands += `Boarding: ${passenger.boarding_point || booking.pickup_point}\n`;
  if (passenger.dropoff_point || booking.dropoff_point) commands += `Drop-off: ${passenger.dropoff_point || booking.dropoff_point}\n`;
  const pType = passenger.is_child_under_5 ? 'Child <5 (FREE)' : passenger.is_complementary ? 'Staff Comp (FREE)' : passenger.is_border_jumper ? 'Border Jumper' : passenger.is_local ? 'Local (Exempt)' : 'Adult';
  commands += `Category: ${pType}\n`;
  if (passenger.sex) commands += `Sex/Age: ${passenger.sex} ${passenger.age ? '(' + passenger.age + 'y)' : ''}\n`;
  commands += '--------------------------------\n';

  commands += CMD.BOLD_ON + 'TRIP DETAILS:\n' + CMD.BOLD_OFF;
  commands += `Origin: ${booking.trip_details?.origin || trip?.origin || 'Harare'}\n`;
  commands += `Destination: ${booking.trip_details?.destination || trip?.destination || 'Durban'}\n`;
  const depTime = booking.departure_datetime || booking.trip_details?.departure || booking.trip_details?.departure_datetime || trip?.departure_datetime || '';
  commands += `Departure: ${depTime ? depTime.slice(0, 16).replace('T', ' ') : 'N/A'}\n`;
  commands += `Bus Reg: ${booking.bus_registration || trip?.bus_registration || 'ADF 2026'}\n`;
  const driverName = booking.driver_name || booking.trip_details?.driver_name || trip?.driver_name || 'Assigned Driver';
  const conductorName = booking.conductor_name || booking.trip_details?.conductor_name || trip?.conductor_name || 'Assigned Host';
  const conductorPhone = booking.conductor_phone || booking.trip_details?.conductor_phone || '';
  commands += `Driver: ${driverName}\n`;
  commands += `Host: ${conductorName}\n`;
  if (conductorPhone) commands += `Host Phone: ${conductorPhone}\n`;
  if (booking.payment_location) commands += `Paid From: ${booking.payment_location}\n`;
  commands += '--------------------------------\n';

  commands += CMD.BOLD_ON + 'PAYMENT SUMMARY:\n' + CMD.BOLD_OFF;
  commands += `Method: ${(booking.payment_method || 'CASH').toUpperCase()}\n`;
  commands += `Status: ${(booking.payment_status || 'PAID').toUpperCase()}\n`;
  if (booking.negotiated_price_applied) {
    commands += `Negotiated Price/Seat: ${booking.currency || 'USD'} ${Number(booking.negotiated_price_per_seat || 0).toFixed(2)}\n`;
    commands += `Standard Price/Seat: ${booking.currency || 'USD'} ${Number(booking.standard_price_per_seat || 0).toFixed(2)}\n`;
    if (booking.negotiation_reason) commands += `Reason: ${booking.negotiation_reason}\n`;
  }
  commands += `Total Fare: ${booking.currency || 'USD'} ${Number(booking.total_amount || 0).toFixed(2)}\n`;
  commands += `Paid Amount: ${booking.currency || 'USD'} ${Number(booking.paid_amount || 0).toFixed(2)}\n`;
  
  if (booking.pay_forward_balance > 0) {
    commands += CMD.BOLD_ON;
    commands += `Station Balance Due: ${booking.currency || 'USD'} ${Number(booking.pay_forward_balance).toFixed(2)}\n`;
    commands += CMD.BOLD_OFF;
  }

  commands += '================================\n';
  commands += CMD.ALIGN_CENTER;
  commands += 'Safe Travel with Panashe Coaches!\n';
  commands += 'PMB: +27843797126\n';
  commands += 'Durban: +27656139181\n';
  commands += 'Mooi River: +27622971043\n';
  commands += 'Harare: +263 786 563 430\n';
  commands += 'www.panashecoaches.com\n\n\n\n';
  const detailsUrl = getSystemDetailsUrl(booking.booking_ref, 'ticket', origin);
  commands += CMD.ALIGN_CENTER;
  commands += encodeQrCommands(detailsUrl);
  commands += '\nScan for ticket details\n';
  commands += `${detailsUrl}\n`;
  commands += CMD.FEED_CUT;

  return encodeText(commands);
}

/**
 * Format luggage ticket into ESC/POS thermal receipt commands
 */
export function formatLuggageEscPos(luggage, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  let commands = '';

  commands += CMD.RESET;
  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += CMD.DOUBLE_SIZE_ON;
  commands += 'PANASHE COACHES\n';
  commands += CMD.DOUBLE_SIZE_OFF;
  commands += 'LUGGAGE / PARCEL TICKET\n';
  commands += '================================\n';
  commands += CMD.BOLD_OFF;

  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += `TAG #: ${luggage.luggage_ref || ''}\n`;
  commands += `STATUS: ${(luggage.status || 'REGISTERED').toUpperCase()}\n`;
  commands += CMD.BOLD_OFF;
  commands += '--------------------------------\n';

  commands += CMD.ALIGN_LEFT;
  commands += `Description: ${luggage.description || 'Luggage Item'}\n`;
  commands += `Quantity: ${luggage.quantity || 1}\n`;
  commands += `Fragile: ${luggage.is_fragile ? 'YES (Handle with Care)' : 'NO'}\n`;
  commands += `Origin: ${luggage.origin || 'Harare'}\n`;
  commands += `Destination: ${luggage.destination || 'N/A'}\n`;
  if (luggage.declared_value > 0) {
    const dCurr = luggage.declared_value_currency === 'USD' ? '$' : 'R';
    commands += `Declared Value: ${dCurr}${Number(luggage.declared_value).toFixed(2)} (${luggage.declared_value_currency || 'ZAR'})\n`;
  }
  commands += '--------------------------------\n';

  commands += CMD.BOLD_ON + 'PAYMENT DETAILS:\n' + CMD.BOLD_OFF;
  const currSym = luggage.currency === 'ZAR' ? 'R' : '$';
  commands += `Total Charge: ${currSym}${Number(luggage.total_charge || 0).toFixed(2)}\n`;
  commands += `Amount Paid: ${currSym}${Number(luggage.amount_paid || 0).toFixed(2)}\n`;
  if (luggage.balance_cleared) {
    commands += CMD.BOLD_ON + `BALANCE: PAID IN FULL\n` + CMD.BOLD_OFF;
    if (luggage.balance_cleared_station) {
      commands += `Cleared at: ${luggage.balance_cleared_station}\n`;
    }
  } else if (luggage.pay_forward_balance > 0) {
    commands += CMD.BOLD_ON + `BAL DUE: ${currSym}${Number(luggage.pay_forward_balance).toFixed(2)} (UNPAID)\n` + CMD.BOLD_OFF;
    commands += `* DO NOT RELEASE UNTIL PAID *\n`;
  }
  commands += '--------------------------------\n';

  commands += CMD.BOLD_ON + 'SENDER:\n' + CMD.BOLD_OFF;
  commands += `Name: ${luggage.sender_name || 'N/A'}\n`;
  commands += `Phone: ${luggage.sender_phone || 'N/A'}\n`;

  if (luggage.receiver_name) {
    commands += '--------------------------------\n';
    commands += CMD.BOLD_ON + 'RECEIVER:\n' + CMD.BOLD_OFF;
    commands += `Name: ${luggage.receiver_name}\n`;
    commands += `Phone: ${luggage.receiver_phone || 'N/A'}\n`;
  }

  commands += '================================\n';
  commands += CMD.ALIGN_CENTER;
  commands += 'Thank you for choosing Panashe!\n';
  commands += 'Support: +263 786 563 430\n\n\n\n';
  const detailsUrl = getSystemDetailsUrl(luggage.luggage_ref, 'luggage', origin);
  commands += CMD.ALIGN_CENTER;
  commands += encodeQrCommands(detailsUrl);
  commands += '\nScan for luggage tracking\n';
  commands += `${detailsUrl}\n`;
  commands += CMD.FEED_CUT;

  return encodeText(commands);
}

/**
 * Format dedicated luggage parcel tag for 58mm / 80mm thermal sticker label
 */
export function formatLuggageTagEscPos(luggage, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  let commands = '';

  commands += CMD.RESET;
  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += CMD.DOUBLE_SIZE_ON;
  commands += 'PANASHE COACHES\n';
  commands += 'LUGGAGE / PARCEL TAG\n';
  commands += CMD.DOUBLE_SIZE_OFF;
  commands += '================================\n';
  commands += CMD.BOLD_OFF;

  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += CMD.DOUBLE_SIZE_ON;
  commands += `${luggage.luggage_ref || ''}\n`;
  commands += CMD.DOUBLE_SIZE_OFF;
  commands += `TAG #${luggage.luggage_number || luggage.luggage_ref}\n`;
  commands += CMD.BOLD_OFF;
  commands += '--------------------------------\n';

  // QR Code — direct URL link pointing to luggage ticket in the system
  const detailsUrl = getSystemDetailsUrl(luggage.luggage_ref, 'luggage', origin);
  commands += CMD.ALIGN_CENTER;
  commands += encodeQrCommands(detailsUrl);
  commands += '\n';

  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += `DEST: ${luggage.custom_destination || luggage.destination || 'N/A'}\n`;
  commands += CMD.BOLD_OFF;
  commands += `Origin: ${luggage.origin || 'Harare'}\n`;
  if (luggage.bus_registration) {
    commands += `Bus: ${luggage.bus_registration}\n`;
  }
  if (luggage.route_name) {
    commands += `Route: ${luggage.route_name}\n`;
  }
  commands += '--------------------------------\n';

  commands += CMD.ALIGN_LEFT;
  commands += `Item: ${luggage.description || 'Parcel'} (Qty: ${luggage.quantity || 1})\n`;
  commands += `Sender: ${luggage.sender_name || 'N/A'} (${luggage.sender_phone || ''})\n`;
  commands += `Receiver: ${luggage.receiver_name || 'Self Collect'} (${luggage.receiver_phone || ''})\n`;

  if (luggage.is_fragile) {
    commands += CMD.BOLD_ON + '*** FRAGILE - HANDLE WITH CARE ***\n' + CMD.BOLD_OFF;
  }
  if (luggage.is_pay_forward && luggage.pay_forward_balance > 0) {
    const currSym = luggage.currency === 'ZAR' ? 'R' : '$';
    commands += CMD.BOLD_ON + `*** BAL DUE: ${currSym}${Number(luggage.pay_forward_balance).toFixed(2)} ***\n` + CMD.BOLD_OFF;
  }

  commands += '================================\n';
  commands += CMD.ALIGN_CENTER;
  commands += 'Attach this tag securely to parcel\n';
  commands += 'Panashe Coaches Logistics\n\n\n\n';
  commands += CMD.FEED_CUT;

  return encodeText(commands);
}

/**
 * Format QR Code Only for thermal label printer (dedicated sticker tag)
 */
export function formatQrCodeOnlyEscPos(luggage, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  let commands = '';

  commands += CMD.RESET;
  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += CMD.DOUBLE_SIZE_ON;
  commands += 'PANASHE COACHES\n';
  commands += `${luggage.luggage_ref || ''}\n`;
  commands += CMD.DOUBLE_SIZE_OFF;
  commands += CMD.BOLD_OFF;
  commands += `Tag #${luggage.luggage_number || luggage.luggage_ref}\n`;
  commands += '--------------------------------\n';

  // BIG CENTERED QR CODE — direct URL link pointing to luggage ticket in the system
  const detailsUrl = getSystemDetailsUrl(luggage.luggage_ref, 'luggage', origin);
  commands += CMD.ALIGN_CENTER;
  commands += encodeQrCommands(detailsUrl);
  commands += '\n';

  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += `DEST: ${luggage.custom_destination || luggage.destination || 'N/A'}\n`;
  commands += CMD.BOLD_OFF;
  commands += `${luggage.origin} -> ${luggage.custom_destination || luggage.destination}\n`;
  if (luggage.bus_registration) {
    commands += `Bus: ${luggage.bus_registration}\n`;
  }
  if (luggage.receiver_name) {
    commands += `Receiver: ${luggage.receiver_name}\n`;
  }
  if (luggage.is_fragile) {
    commands += CMD.BOLD_ON + '*** FRAGILE ***\n' + CMD.BOLD_OFF;
  }
  commands += '================================\n\n\n\n';
  commands += CMD.FEED_CUT;

  return encodeText(commands);
}

/**
 * Format Ticket Information Only (without large QR Code tag, focused on passenger/trip details)
 */
export function formatTicketInfoOnlyEscPos(luggage, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  let commands = '';

  commands += CMD.RESET;
  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += CMD.DOUBLE_SIZE_ON;
  commands += 'PANASHE COACHES\n';
  commands += CMD.DOUBLE_SIZE_OFF;
  commands += 'LUGGAGE / PARCEL RECEIPT\n';
  commands += '================================\n';
  commands += CMD.BOLD_OFF;

  commands += CMD.ALIGN_CENTER;
  commands += CMD.BOLD_ON;
  commands += `REF: ${luggage.luggage_ref || ''}\n`;
  commands += `STATUS: ${(luggage.status || 'REGISTERED').toUpperCase()}\n`;
  commands += CMD.BOLD_OFF;
  commands += '--------------------------------\n';

  commands += CMD.ALIGN_LEFT;
  commands += CMD.BOLD_ON + 'ITEM DETAILS:\n' + CMD.BOLD_OFF;
  commands += `Description: ${luggage.description || 'Luggage Item'}\n`;
  commands += `Quantity: ${luggage.quantity || 1}\n`;
  commands += `Fragile: ${luggage.is_fragile ? 'YES (Handle with Care)' : 'NO'}\n`;
  commands += `Origin: ${luggage.origin || 'Harare'}\n`;
  commands += `Destination: ${luggage.custom_destination || luggage.destination || 'N/A'}\n`;
  if (luggage.bus_registration) {
    commands += `Bus: ${luggage.bus_registration}\n`;
  }
  if (luggage.route_name) {
    commands += `Route: ${luggage.route_name}\n`;
  }
  if (luggage.departure_datetime) {
    const dep = luggage.departure_datetime.slice(0, 16).replace('T', ' ');
    commands += `Departure: ${dep}\n`;
  }
  if (luggage.declared_value > 0) {
    const dCurr = luggage.declared_value_currency === 'USD' ? '$' : 'R';
    commands += `Declared Value: ${dCurr}${Number(luggage.declared_value).toFixed(2)} (${luggage.declared_value_currency || 'ZAR'})\n`;
  }
  commands += '--------------------------------\n';

  commands += CMD.BOLD_ON + 'SENDER:\n' + CMD.BOLD_OFF;
  commands += `Name: ${luggage.sender_name || 'N/A'}\n`;
  commands += `Phone: ${luggage.sender_phone || 'N/A'}\n`;
  if (luggage.sender_id_number) {
    commands += `ID: ${luggage.sender_id_number}\n`;
  }

  if (luggage.receiver_name) {
    commands += '--------------------------------\n';
    commands += CMD.BOLD_ON + 'RECEIVER:\n' + CMD.BOLD_OFF;
    commands += `Name: ${luggage.receiver_name}\n`;
    commands += `Phone: ${luggage.receiver_phone || 'N/A'}\n`;
  }

  commands += '--------------------------------\n';
  commands += CMD.BOLD_ON + 'PAYMENT SUMMARY:\n' + CMD.BOLD_OFF;
  const currSym = luggage.currency === 'ZAR' ? 'R' : '$';
  commands += `Total Fare: ${currSym}${Number(luggage.total_charge || 0).toFixed(2)}\n`;
  commands += `Amount Paid: ${currSym}${Number(luggage.amount_paid || 0).toFixed(2)}\n`;
  if (luggage.balance_cleared) {
    commands += CMD.BOLD_ON + `Balance: PAID IN FULL\n` + CMD.BOLD_OFF;
    if (luggage.balance_cleared_station) {
      commands += `Station: ${luggage.balance_cleared_station}\n`;
    }
  } else if (luggage.pay_forward_balance > 0) {
    commands += CMD.BOLD_ON + `BAL DUE: ${currSym}${Number(luggage.pay_forward_balance).toFixed(2)} (UNPAID)\n` + CMD.BOLD_OFF;
    commands += `* DO NOT RELEASE UNTIL PAID *\n`;
  }

  commands += '================================\n';
  commands += CMD.ALIGN_CENTER;
  commands += 'Thank you for choosing Panashe!\n';
  commands += 'Harare: +263 786 563 430\n';
  commands += 'Durban: +27 656 139 181\n';
  commands += 'www.panashecoaches.com\n\n\n\n';
  commands += CMD.FEED_CUT;

  return encodeText(commands);
}

let cachedBluetoothPrinter = null;
let dualPrinter1 = null;
let dualPrinter2 = null;

export function getCachedBluetoothPrinter() {
  return cachedBluetoothPrinter;
}

export function clearCachedBluetoothPrinter() {
  cachedBluetoothPrinter = null;
}

export function getDualPrinter1() {
  return dualPrinter1 && dualPrinter1.gatt && dualPrinter1.gatt.connected ? dualPrinter1 : dualPrinter1;
}

export function setDualPrinter1(dev) {
  dualPrinter1 = dev;
}

export function getDualPrinter2() {
  return dualPrinter2 && dualPrinter2.gatt && dualPrinter2.gatt.connected ? dualPrinter2 : dualPrinter2;
}

export function setDualPrinter2(dev) {
  dualPrinter2 = dev;
}

export function swapDualPrinters() {
  const tmp = dualPrinter1;
  dualPrinter1 = dualPrinter2;
  dualPrinter2 = tmp;
  return { printer1: dualPrinter1, printer2: dualPrinter2 };
}

/**
 * Prompt user to pair a Bluetooth thermal printer on a direct click
 */
export async function pairBluetoothPrinter() {
  if (!navigator.bluetooth) {
    throw new Error('Web Bluetooth is not supported by this browser. Please use Chrome or Edge on Windows/Android/Mac.');
  }

  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: [
      '000018f0-0000-1000-8000-00805f9b34fb', // Standard ESC/POS service
      '00001101-0000-1000-8000-00805f9b34fb', // Serial Port Profile (SPP)
      'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Bluetooth POS service
      '49535343-fe7d-4ae5-8fa9-9fafd205e455'  // ISSC transparent service
    ]
  });

  if (!device.gatt.connected) {
    await device.gatt.connect();
  }

  return device;
}

/**
 * Send ESC/POS bytes to an existing connected Bluetooth device
 */
export async function sendBytesToBluetoothDevice(device, dataBytes) {
  if (!device) {
    throw new Error('No Bluetooth device specified');
  }

  const server = device.gatt.connected ? device.gatt : await device.gatt.connect();
  const services = await server.getPrimaryServices();

  if (!services || services.length === 0) {
    throw new Error(`No Bluetooth services found on ${device.name || 'printer'}.`);
  }

  let writeCharacteristic = null;
  for (const service of services) {
    try {
      const characteristics = await service.getCharacteristics();
      for (const char of characteristics) {
        if (char.properties.write || char.properties.writeWithoutResponse) {
          writeCharacteristic = char;
          break;
        }
      }
    } catch (e) {
      // continue searching
    }
    if (writeCharacteristic) break;
  }

  if (!writeCharacteristic) {
    throw new Error(`Could not find a writable characteristic on ${device.name || 'printer'}.`);
  }

  // Stream data in 100-byte chunks
  const CHUNK_SIZE = 100;
  for (let i = 0; i < dataBytes.length; i += CHUNK_SIZE) {
    const chunk = dataBytes.slice(i, i + CHUNK_SIZE);
    if (writeCharacteristic.properties.writeWithoutResponse) {
      await writeCharacteristic.writeValueWithoutResponse(chunk);
    } else {
      await writeCharacteristic.writeValue(chunk);
    }
    await new Promise(r => setTimeout(r, 40));
  }

  return true;
}

/**
 * Print simultaneously to 2 Bluetooth Thermal Printers at the exact same time
 */
export async function printToDualBluetoothThermalPrinters(deviceTicket, deviceQr, luggage, origin = '') {
  const resolvedOrigin = origin || (typeof window !== 'undefined' && window.location ? window.location.origin : '');
  const ticketBytes = formatTicketInfoOnlyEscPos(luggage, resolvedOrigin);
  const qrBytes = formatQrCodeOnlyEscPos(luggage, resolvedOrigin);

  // Send to both printers in parallel!
  return await Promise.all([
    sendBytesToBluetoothDevice(deviceTicket, ticketBytes),
    sendBytesToBluetoothDevice(deviceQr, qrBytes)
  ]);
}

/**
 * Connect to Bluetooth Thermal Printer & Send Byte Stream
 */
export async function printBluetoothEscPos(dataBytes, existingDevice = null) {
  if (!navigator.bluetooth) {
    throw new Error('Web Bluetooth is not supported by this browser. Please use Chrome or Edge on Windows/Android/Mac.');
  }

  try {
    const device = existingDevice || 
      (cachedBluetoothPrinter && cachedBluetoothPrinter.gatt && cachedBluetoothPrinter.gatt.connected ? cachedBluetoothPrinter : null) ||
      await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb', // Standard ESC/POS service
          '00001101-0000-1000-8000-00805f9b34fb', // Serial Port Profile (SPP)
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Bluetooth POS service
          '49535343-fe7d-4ae5-8fa9-9fafd205e455'  // ISSC transparent service
        ]
      });

    cachedBluetoothPrinter = device;

    await sendBytesToBluetoothDevice(device, dataBytes);
    return device;
  } catch (err) {
    console.error('Bluetooth Print Error:', err);
    throw err;
  }
}
